import React, { useState, useEffect, useMemo } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import Pagination from "./Pagination";
import moment from "moment";
import { FaSpinner, FaExchangeAlt } from "react-icons/fa";
import { API_URL } from "../constants/index";
import { GetServiceCaller, PostServiceCaller } from "../services/serviceCaller";
import { toast } from "react-toastify";

const CandidateDetailsModal = ({
  showModal,
  handleCloseModal,
  modalData = [],
  modalContent = null,
  modalLoading = false,
  isRmgLead = false,
  recruiterTitle,
  refreshData = null,
}) => {
  const [modalHrFilter, setModalHrFilter] = useState(null);
  const [currentPages, setCurrentPages] = useState(1);
  const itemsPerPages = 5;

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferCandidateItem, setTransferCandidateItem] = useState(null);
  const [openRequisitions, setOpenRequisitions] = useState([]);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);

  const getName = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (value.props && typeof value.props.children === "string") {
      return value.props.children.trim();
    }
    return "";
  };

  const fetchOpenRequisitions = async () => {
    setLoadingRequisitions(true);
    try {
      const response = await GetServiceCaller(`${API_URL}get-open-requisitions`);
      if (response && response.status === 200) {
        const data = response?.data?.data || [];
        const currentReq = modalContent ? getName(modalContent.RequisitionID) : "";
        const filtered = data
          .filter(req => req.RequisitionID && req.RequisitionID.trim() !== "" && req.RequisitionID !== currentReq)
          .sort((a, b) => {
            const hasA = !!a.RequisitionID;
            const hasB = !!b.RequisitionID;
            if (hasA && !hasB) return -1;
            if (!hasA && hasB) return 1;
            const titleA = a.Title ? a.Title.toLowerCase() : "";
            const titleB = b.Title ? b.Title.toLowerCase() : "";
            return titleA.localeCompare(titleB);
          })
          .map(req => ({
            value: req.JobDescriptionID,
            label: req.RequisitionID ? `${req.Title} (${req.RequisitionID})` : req.Title
          }));
        setOpenRequisitions(filtered);
      } else {
        toast.error("Failed to fetch open requisitions.");
      }
    } catch (error) {
      console.error("Error fetching open requisitions:", error);
      toast.error("Error fetching open requisitions.");
    } finally {
      setLoadingRequisitions(false);
    }
  };

  const handleOpenTransfer = (item) => {
    setTransferCandidateItem(item);
    setSelectedRequisition(null);
    setShowTransferModal(true);
    fetchOpenRequisitions();
  };

  const handleTransferSubmit = async () => {
    if (!selectedRequisition) {
      toast.warning("Please select a new requisition.");
      return;
    }

    setTransferLoading(true);
    try {
      const payload = {
        candidateId: transferCandidateItem.id || transferCandidateItem.ID || transferCandidateItem.LeadCount,
        newRequisitionId: String(selectedRequisition.value)
      };

      const response = await PostServiceCaller(`${API_URL}transfer-candidate`, payload, "post");
      if (response && response.status === 200) {
        toast.success("Candidate transferred successfully.");
        setShowTransferModal(false);
        if (refreshData) {
          refreshData();
        }
      } else {
        toast.error(response?.data?.message || "Transfer failed.");
      }
    } catch (error) {
      console.error("Error transferring candidate:", error);
      toast.error(error?.response?.data?.message || "Error transferring candidate.");
    } finally {
      setTransferLoading(false);
    }
  };

  // Extract unique recruiter options (allocated TAs + TAs involved in adding leads)
  const modalHrOptions = useMemo(() => {
    let recruiters = [];
    if (Array.isArray(modalData)) {
      recruiters = modalData
        .map((item) => item.RecruiterName)
        .filter((name) => name && name.trim() !== "");
    }

    if (modalContent) {
      const getName = (value) => {
        if (!value) return '';
        // If it's a string, trim directly
        if (typeof value === 'string') return value.trim();
        // If it's a React element, attempt to get its children text
        if (value.props && typeof value.props.children === 'string') {
          return value.props.children.trim();
        }
        return '';
      };
      const primary = getName(modalContent.PrimaryHRName);
      if (primary) recruiters.push(primary);
      const secondary = getName(modalContent.SecondaryHRName);
      if (secondary) recruiters.push(secondary);
    }

    const uniqueRecruiters = [...new Set(recruiters)];
    return uniqueRecruiters.map((name) => ({ value: name, label: name }));
  }, [modalData, modalContent]);

  // Filter modal candidate data by selected recruiter
  const filteredModalData = useMemo(() => {
    if (!Array.isArray(modalData)) return [];
    if (!modalHrFilter || !modalHrFilter.value) return modalData;
    return modalData.filter((item) => item.RecruiterName === modalHrFilter.value);
  }, [modalData, modalHrFilter]);

  // Reset filter and page when modal opens or content changes
  useEffect(() => {
    if (showModal) {
      setModalHrFilter(null);
      setCurrentPages(1);
    }
  }, [showModal, modalContent]);

  const totalItems = filteredModalData ? filteredModalData.length : 0;
  const indexOfLastItem = currentPages * itemsPerPages;
  const indexOfFirstItem = indexOfLastItem - itemsPerPages;
  const currentItems = filteredModalData?.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChanges = (pageNumber) => {
    setCurrentPages(pageNumber);
  };

  if (!showModal) return null;

  return (
    <div
      className="modal"
      tabIndex="-1"
      role="dialog"
      style={{
        display: "block",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5
              className="heading"
              style={{ fontWeight: "800", color: "#2F2E4B", marginLeft: "20px" }}
            >
              Candidate Details
            </h5>
            <div className="d-flex align-items-center">
              <div className="me-3" style={{ width: "230px" }}>
                <Select
                  value={modalHrFilter}
                  onChange={setModalHrFilter}
                  options={[
                    { value: "", label: recruiterTitle ? `All ${recruiterTitle}s` : (isRmgLead ? "All RMG POCs" : "All Recruiters") },
                    ...modalHrOptions,
                  ]}
                  placeholder={recruiterTitle ? `Filter by ${recruiterTitle}...` : (isRmgLead ? "Filter by RMG POC..." : "Filter by Recruiter...")}
                  isClearable
                  className="w-100"
                />
              </div>
              <span className="me-2">
                <i
                  className="bi bi-x-lg"
                  style={{ cursor: "pointer" }}
                  onClick={handleCloseModal}
                ></i>
              </span>
            </div>
          </div>

          {/* Modal Body */}
          <div
            className="modal-body"
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              backgroundColor: "white",
            }}
          >
            {(() => {
              const cellStyle = {
                padding: "4px 6px",
                fontSize: "12px",
                verticalAlign: "middle",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap"
              };
              return isRmgLead ? (
                <div className="table-responsive" style={{ width: "100%" }}>
                  <Table striped bordered hover style={{ tableLayout: "fixed", width: "900px", margin: 0 }}>
                    <thead id="thcolor" className="text-center">
                      <tr>
                        <th style={{ ...cellStyle, width: "250px" }}>Candidate Name</th>
                        <th style={{ ...cellStyle, width: "250px" }}>{recruiterTitle || "RMG POC"}</th>
                        <th style={{ ...cellStyle, width: "180px" }}>Applied Date</th>
                        <th style={{ ...cellStyle, width: "220px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalLoading ? (
                        <tr>
                          <td colSpan={4} style={{ ...cellStyle, textAlign: "center", padding: "20px" }}>
                            <FaSpinner className="spinner" />
                            <br />
                            Loading...
                          </td>
                        </tr>
                      ) : Array.isArray(currentItems) && currentItems.length > 0 ? (
                        currentItems.map((item) => (
                          <tr key={item.id || item.ID || item.LeadCount}>
                            <td style={cellStyle} title={item.FullName}>{item.FullName}</td>
                            <td style={cellStyle} title={item.RecruiterName}>{item.RecruiterName}</td>
                            <td style={cellStyle}>
                              {moment(item.AppliedDate).format("DD-MMM-YYYY")}
                            </td>
                            <td style={cellStyle} title={item.Status}>{item.Status}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ ...cellStyle, textAlign: "center" }}>
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="table-responsive" style={{ width: "100%" }}>
                  <Table striped bordered hover style={{ tableLayout: "fixed", width: "1010px", margin: 0 }}>
                    <thead id="thcolor" className="text-center">
                      <tr>
                        <th style={{ ...cellStyle, width: "150px" }}>Candidate Name</th>
                        <th style={{ ...cellStyle, width: "150px" }}>Skillset</th>
                        <th style={{ ...cellStyle, width: "100px" }}>Source</th>
                        <th style={{ ...cellStyle, width: "120px" }}>Referred By</th>
                        <th style={{ ...cellStyle, width: "150px" }}>Recruiter Name</th>
                        <th style={{ ...cellStyle, width: "110px" }}>Applied Date</th>
                        <th style={{ ...cellStyle, width: "150px" }}>Status</th>
                        <th style={{ ...cellStyle, width: "80px", textAlign: "center" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalLoading ? (
                        <tr>
                          <td colSpan={8} style={{ ...cellStyle, textAlign: "center", padding: "20px" }}>
                            <FaSpinner className="spinner" />
                            <br />
                            Loading...
                          </td>
                        </tr>
                      ) : Array.isArray(currentItems) && currentItems.length > 0 ? (
                        currentItems.map((item) => (
                          <tr key={item.id || item.ID || item.LeadCount}>
                            <td style={cellStyle} title={item.FullName}>{item.FullName}</td>
                            <td style={{ ...cellStyle, width: "150px", maxWidth: "150px" }}>
                              {(() => {
                                const rawSkills = item.Skillset;
                                if (!rawSkills) return "-";
                                const formattedSkills = rawSkills
                                  .replace(/,+/g, ", ")
                                  .replace(/,\s*$/, "")
                                  .trim();
                                return (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id={`tooltip-skills-${item.id || item.ID || item.LeadCount}`}>
                                        {formattedSkills}
                                      </Tooltip>
                                    }
                                  >
                                    <span
                                      style={{
                                        display: "block",
                                        maxWidth: "135px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        cursor: "pointer"
                                      }}
                                    >
                                      {formattedSkills}
                                    </span>
                                  </OverlayTrigger>
                                );
                              })()}
                            </td>
                            <td style={cellStyle} title={item.Source || "-"}>{item.Source || "-"}</td>
                            <td style={cellStyle} title={item.ReferredBy || "-"}>{item.ReferredBy || "-"}</td>
                            <td style={cellStyle} title={item.RecruiterName}>{item.RecruiterName}</td>
                            <td style={cellStyle}>
                              {moment(item.AppliedDate).format("DD-MMM-YYYY")}
                            </td>
                            <td style={cellStyle} title={item.Status}>{item.Status}</td>
                            <td style={{ ...cellStyle, textAlign: "center" }}>
                              {(() => {
                                const statusStr = (item.Status || "").trim().toLowerCase();
                                const isTransferDisabled = statusStr === "on board" || statusStr === "blacklisted";
                                return isTransferDisabled ? (
                                  <FaExchangeAlt
                                    className="text-muted"
                                    style={{ opacity: 0.4, cursor: "not-allowed" }}
                                    title={`Cannot transfer candidate in "${item.Status}" status`}
                                  />
                                ) : (
                                  <FaExchangeAlt
                                    className="text-primary"
                                    style={{ cursor: "pointer" }}
                                    title="Transfer Candidate"
                                    onClick={() => handleOpenTransfer(item)}
                                  />
                                );
                              })()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} style={{ ...cellStyle, textAlign: "center" }}>
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              );
            })()}
            <div className="d-flex justify-content-end">
              <Pagination
                itemsPerPage={itemsPerPages}
                totalItems={totalItems}
                currentPage={currentPages}
                onPageChange={handlePageChanges}
              />
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer d-flex justify-content-end">
            <button className="custom-btn" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Transfer Candidate Sub-Modal */}
      {showTransferModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "8px", boxShadow: "0 5px 15px rgba(0,0,0,0.3)" }}>
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="heading" style={{ fontWeight: "800", color: "#2F2E4B", margin: 0 }}>
                  Transfer Candidate
                </h5>
                <span className="me-2">
                  <i
                    className="bi bi-x-lg"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowTransferModal(false)}
                  ></i>
                </span>
              </div>
              <div className="modal-body text-start">
                <div className="mb-3">
                  <label className="form-label fw-bold">Current Requisition ID</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={modalContent ? getName(modalContent.RequisitionID) : ""}
                    disabled
                  />
                </div>
                <div className="mb-3" style={{ position: "relative", zIndex: 1070 }}>
                  <label className="form-label fw-bold text-start d-block">Select New Requisition <span className="text-danger">*</span></label>
                  <Select
                    value={selectedRequisition}
                    onChange={setSelectedRequisition}
                    options={openRequisitions}
                    placeholder="Search open requisitions..."
                    isLoading={loadingRequisitions}
                    isSearchable
                  />
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-end gap-2">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowTransferModal(false)}
                  disabled={transferLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleTransferSubmit}
                  disabled={transferLoading}
                  style={{ backgroundColor: "#27568B", borderColor: "#27568B" }}
                >
                  {transferLoading ? "Transferring..." : "Transfer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDetailsModal;
